import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import type { ITenantUsageData } from '@experiences/interfaces';
import {
    roundTo2Decimals,
    useNavigateWithParams,
    useRouteResolver,
    useShowDialog,
} from '@experiences/util';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import { IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import {
    createStyles,
    makeStyles,
} from '@mui/styles';
import { ApBadge } from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { Row } from 'react-table';
import useSWR from 'swr';

import { hyperLink } from '../../../common/constants/Constant';
import * as RouteNames from '../../../common/constants/RouteNames';
import AllocateLicenseIcon from '../../../images/icons/AllocateLicenseIcon';
import {
    getTenantUsageData,
    licenseUsageUrl,
} from '../../../services/licensing/LicenseUsageService';
import { accountGlobalId } from '../../../store/selectors';
import { UiGrid } from '../../common/UiGrid';
import {
    ButtonType,
    GridActionType,
} from '../../common/UiGrid/grid';
import { isTenantAllocatableConsumable } from '../../tenants/subcomponents/helpers/AllocationSectionsHelper';
import { UiChartLoading } from '../helperComponents/UiConsumableCard/UiChartLoading';
import { usePreviousLocationState } from '../hooks/usePreviousLocationState';
import { useCanManageLicensingData } from '../useCanManageLicensingData';
import { useLicensingManagePermissionTooltip } from '../useLicensingPermissionTooltip';
import { AllocationEnforcementDialogBody } from './AllocationEnforcementDialog';

const useStyles = makeStyles(theme =>
    createStyles({
        organizationLevelCell: {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
        },
        tenantNameCell: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: '100%',
        },
        documentationLink: {
            color: 'inherit',
            textDecoration: 'underline',
        },
        infoIconButton: { padding: '2px' },
    }),
);

interface ITenantUsage {
    tenantId: string;
    tenantName: string;
    allocated: number;
    consumed: number;
    isOrganizationLevel?: boolean;
    isDeleted: boolean;
    isDisabled: boolean;
}

export const TenantUsageGrid: React.FC<{
    consumableCode: string;
    tenantId?: string;
    startDate: number | undefined;
    endDate: number | undefined;
}> = ({
    consumableCode, tenantId, startDate, endDate,
}) => {
    const classes = useStyles();
    const navigate = useNavigateWithParams();
    const previousLocationState = usePreviousLocationState();
    const getRoute = useRouteResolver();
    const { formatMessage: translate } = useIntl();
    const createDialog = useShowDialog();
    const organizationId = useSelector(accountGlobalId);

    // Feature flag to show/hide allocation enforcement configuration
    const enableAllocationEnforcement = useFeatureFlagValue(Features.EnableAllocationEnforcement.name);
    const canManageLicensingData = useCanManageLicensingData();
    const managePermissionTooltip = useLicensingManagePermissionTooltip();

    const handleOpenModal = useCallback(
        async (tenant: ITenantUsage) => {
            await createDialog({
                title: translate({ id: 'CLIENT_ALLOCATION_ENFORCEMENT' }),
                customDialogContent: AllocationEnforcementDialogBody,
                customDialogContentProps: {
                    tenantId: tenant.tenantId,
                    tenantName: tenant.tenantName,
                    consumableCode,
                },
            });
        },
        [ consumableCode, createDialog, translate ],
    );

    const { data: tenantUsageData } = useSWR(
        {
            url: `${licenseUsageUrl}/tenants`,
            startDate,
            endDate,
            consumableCode,
            tenantId,
            accountGlobalId: organizationId,
        },
        getTenantUsageData,
    );

    const transformedUsageData: ITenantUsage[] | undefined = useMemo(() => {
        if (!tenantUsageData) {
            return undefined;
        }

        const tenantUsageList: ITenantUsage[] = tenantUsageData.consumptionData.map((tenantData: ITenantUsageData) => ({
            tenantId: tenantData.tenantId,
            tenantName: tenantData.tenantName,
            allocated: tenantData.allocated,
            consumed: roundTo2Decimals(tenantData.consumed),
            isOrganizationLevel: tenantData.tenantId === organizationId,
            isDeleted: tenantData.isDeleted ?? false,
            isDisabled: tenantData.isDisabled ?? false,
        }));

        if (tenantId) {
            return tenantUsageList.filter(tenant => tenant.tenantId === tenantId);
        }

        return tenantUsageList;
    }, [ tenantId, tenantUsageData, organizationId ]);

    const DEFAULT_PAGE_SIZE = 25;
    const [ pageIndex, setPageIndex ] = useState(0);
    const [ pageSize, setPageSize ] = useState(DEFAULT_PAGE_SIZE);

    const totalCount = transformedUsageData?.length ?? 0;
    const pagedData = useMemo(() => {
        if (!transformedUsageData) {
            return [];
        }
        const start = pageIndex * pageSize;
        return transformedUsageData.slice(start, start + pageSize);
    }, [ transformedUsageData, pageIndex, pageSize ]);

    const onPageChange = useCallback((newPageIndex: number, newPageSize: number) => {
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setPageIndex(0);
        } else {
            setPageIndex(newPageIndex);
        }
    }, [ pageSize ]);

    if (!tenantUsageData) {
        return <UiChartLoading
            width={150}
            height={36} />;
    }

    return (
        <>
            <div id="ui-grid-section">
                <UiGrid<ITenantUsage>
                    dataCy="tenant-usage-ui-grid"
                    tableHeight={`${60 * pagedData.length + 125}px`}
                    defaultPageSize={pageSize}
                    paginationProps={{
                        onPageChange,
                        onSearch: () => {},
                        onSort: () => {},
                        total: totalCount,
                        sort: {},
                        query: {
                            current: undefined,
                            set: () => {},
                        },
                        page: {
                            current: pageIndex,
                            set: setPageIndex,
                        },
                    }}
                    initialSort={[ { id: 'tenantName' }, { id: 'consumed' } ]}
                    columns={[
                        {
                            accessor: 'tenantName',
                            Header: translate({ id: 'CLIENT_CONSUMABLE_PLATFORM_UNITS_USAGE_TENANT_NAME' }),
                            disableSortBy: false,
                            width: 15,
                            Cell: ({ row }) => {
                                if (row.original.isOrganizationLevel) {
                                    return (
                                        <div className={classes.organizationLevelCell}>
                                            <span>
                                                {translate({ id: 'CLIENT_ORGANIZATION_LEVEL_CONSUMPTION' })}
                                            </span>
                                            <Tooltip
                                                title={
                                                    <span>
                                                        {translate({ id: 'CLIENT_ORGANIZATION_LEVEL_CONSUMPTION_TOOLTIP' })}
                                                        {' '}
                                                        <a
                                                            href={hyperLink.ORGANIZATION_LEVEL_CONSUMPTION}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={classes.documentationLink}>
                                                            {translate({ id: 'CLIENT_SEE_DETAILS_IN_DOCUMENTATION' })}
                                                        </a>
                                                    </span>
                                                }
                                                arrow
                                            >
                                                <IconButton
                                                    size="small"
                                                    className={classes.infoIconButton}>
                                                    <InfoOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    );
                                }
                                if (row.original.isDeleted) {
                                    return (
                                        <div className={classes.tenantNameCell}>
                                            <span>
                                                {row.original.tenantName}
                                            </span>
                                            <ApBadge
                                                label={translate({ id: 'CLIENT_DELETED' })}
                                                status="error"
                                                data-cy="tenant-status-badge-deleted" />
                                        </div>
                                    );
                                }
                                if (row.original.isDisabled) {
                                    return (
                                        <div className={classes.tenantNameCell}>
                                            <span>
                                                {row.original.tenantName}
                                            </span>
                                            <ApBadge
                                                label={translate({ id: 'CLIENT_DISABLED' })}
                                                status="warning"
                                                data-cy="tenant-status-badge-disabled" />
                                        </div>
                                    );
                                }
                                return row.original.tenantName;
                            },
                        },
                        {
                            accessor: 'allocated',
                            Header: translate({ id: 'CLIENT_CONSUMABLE_PLATFORM_UNITS_USAGE_TENANT_ALLOCATED' }),
                            disableSortBy: false,
                            width: 15,
                            Cell: ({ row }) => {
                                if (row.original.isOrganizationLevel) {
                                    return '-';
                                }
                                return row.original.allocated;
                            },
                        },
                        {
                            accessor: 'consumed',
                            Header: translate({ id: 'CLIENT_CONSUMABLE_PLATFORM_UNITS_USAGE_TENANT_CONSUMED' }),
                            width: 15,
                        },
                    ]}
                    rowActions={[
                        {
                            type: ButtonType.Icon,
                            label: translate({ id: 'CLIENT_EDIT_ALLOCATION' }),
                            tooltip: (row?: Row<ITenantUsage>) => (!canManageLicensingData && !row?.original.isDeleted && !row?.original.isDisabled
                                ? managePermissionTooltip
                                : translate({ id: 'CLIENT_EDIT_ALLOCATION' })),
                            actionType: GridActionType.Row,
                            icon: <AllocateLicenseIcon data-cy="ui-grid-edit-tenant-allocations-button" />,
                            click: row => {
                                navigate(`${getRoute(RouteNames.Licensing)}/configure/${row.original.tenantId.toLowerCase()}`,
                                    { state: previousLocationState },
                                );
                            },
                            invisible: (row?: Row<ITenantUsage>) => !!row?.original.isOrganizationLevel,
                            disable: (row?: Row<ITenantUsage>) => !canManageLicensingData || !!row?.original.isDeleted || !!row?.original.isDisabled,
                        },
                        ...(enableAllocationEnforcement && isTenantAllocatableConsumable(consumableCode) ? [
                            {
                                type: ButtonType.Icon,
                                label: translate({ id: 'CLIENT_CONFIGURE_ALLOCATION_ENFORCEMENT' }),
                                tooltip: (row?: Row<ITenantUsage>) => (!canManageLicensingData && !row?.original.isDeleted && !row?.original.isDisabled
                                    ? managePermissionTooltip
                                    : translate({ id: 'CLIENT_CONFIGURE_ALLOCATION_ENFORCEMENT' })),
                                actionType: GridActionType.Row,
                                icon: <SettingsIcon data-cy="ui-grid-configure-enforcement-button" />,
                                click: (row: Row<ITenantUsage>) => {
                                    handleOpenModal(row.original);
                                },
                                invisible: (row?: Row<ITenantUsage>) => !!row?.original.isOrganizationLevel,
                                disable: (row?: Row<ITenantUsage>) => !canManageLicensingData || !!row?.original.isDeleted || !!row?.original.isDisabled,
                            },
                        ] : []),
                    ]}
                    data={pagedData}
                />
            </div>
        </>
    );
};
